import { Link, NavLink, Outlet } from 'react-router-dom';
import { useSession } from '../lib/session.js';
import { useClientContext } from '../lib/clientContext.js';
import { useI18n } from '../lib/i18n.js';
import {
  ScopeProvider,
  useScopeContext,
  type Scope,
} from '../lib/scopeContext.js';
import { SURFACES, SURFACE_CAPABILITY, type SurfaceId } from '../features/nav.js';
import { AdvisorChat } from '../features/advisor/AdvisorChat.js';
import { ConnectionRollup } from '../components/ConnectionRollup.js';
import { ScopeSwitcher } from '../components/ScopeSwitcher.js';
import { useStoreBannerOptions } from '../queries/index.js';
import { Logo } from '../components/Logo.js';
import {
  IconBack,
  IconBrands,
  IconCampaigns,
  IconClients,
  IconConnections,
  IconIngestion,
  IconPayouts,
  IconSettings,
} from '../components/icons.js';

/**
 * The persistent app shell: a left rail of real navigation LINKS + a topbar, with
 * an <Outlet> for the routed page. Because the nav items are router links and the
 * selected client lives in the URL (`/c/:clientId/...`), browser back/forward,
 * open-in-new-tab, and deep links all work.
 *
 * Two-level IA:
 * - ADMIN top level (`/clients`, `/brands`, `/settings`) — "Manage".
 * - CLIENT context (`/c/:clientId/...`) — "Workspace": the scope switcher drives
 *   a scope-aware nav built from the SURFACES registry (clarity-navigation-scope
 *   task 4.2). Role gating + server-side tenant isolation still hold.
 *
 * ── Why the rail wraps its workspace nav in a ScopeProvider (design §4 note) ──
 * The per-route `<ScopeProvider>` lives INSIDE the routed `<Outlet>` (one per
 * scope-branch route in `AppRoutes`), so the Shell — the layout ABOVE that
 * Outlet — can't consume that instance. Rather than duplicate scope resolution
 * in the Shell, the rail's workspace region mounts its OWN `<ScopeProvider>`
 * ({@link WorkspaceRail}). The provider is PURE — it derives everything from the
 * URL via `useMatch` + the loaded brand/store lists — so a rail-side instance and
 * the Outlet-side instance resolve to exactly the same scope (one source of truth
 * for the logic, read in two independent spots). The switcher and the nav both
 * read that single context.
 */
export function Shell() {
  const { session, role, signOut } = useSession();
  const { inClientContext, selectedClient } = useClientContext();
  const { t, toggleLocale, localeTag } = useI18n();
  const isAdmin = role === 'admin';

  return (
    <div className="app">
      <aside className="rail" aria-label="Primary navigation">
        <div className="rail__brand">
          <Logo className="logo" variant="compact" />
          <span className="wordmark">Clarity</span>
        </div>

        {isAdmin && inClientContext && selectedClient && (
          <div className="rail__context">
            <span className="rail__context-label">{t('rail.viewing')}</span>
            <span className="rail__context-name">{selectedClient.name}</span>
          </div>
        )}

        {inClientContext ? (
          // Inside a client: the scope switcher + the scope-driven workspace nav,
          // both reading the rail-side ScopeProvider (see the component note).
          <ScopeProvider>
            <WorkspaceRail />
          </ScopeProvider>
        ) : (
          // Admin top level: the fixed "Manage" nav, no scope.
          <nav className="rail__nav" aria-label={t('rail.manage')}>
            <span className="rail__section-label">{t('rail.manage')}</span>
            <RailLink to="/clients" icon={<IconClients className="nav-ico" />} label={t('nav.clients')} />
            <RailLink to="/brands" icon={<IconBrands className="nav-ico" />} label={t('nav.brands')} />
            <RailLink to="/settings" icon={<IconSettings className="nav-ico" />} label={t('nav.settings')} />
            {/*
             * The ingestion ops panel is ADMIN-ONLY (admin-ingestion-control Req
             * 5.4). This whole "Manage" nav is the admin top level (reached only
             * when !inClientContext, which a client-role user never is), but gate
             * the entry on `isAdmin` explicitly so the hidden-nav contract holds
             * regardless; the route guard in AppRoutes is the real backstop.
             */}
            {isAdmin && (
              <RailLink
                to="/ingestion"
                icon={<IconIngestion className="nav-ico" />}
                label={t('nav.ingestion')}
              />
            )}
            {/*
             * The monthly-offer review surface is ADMIN-ONLY too
             * (clarity-campaign-matching Req 10.3): the admin OCRs the AM offer
             * sheet, reviews/corrects it, and saves it shared across the
             * platform's stores. Same hidden-nav + route-guard contract as the
             * ingestion panel; the server enforces admin-only independently.
             */}
            {isAdmin && (
              <RailLink
                to="/monthly-offers"
                icon={<IconCampaigns className="nav-ico" />}
                label={t('nav.monthlyOffers')}
              />
            )}
            {/*
             * Banner options are ADMIN-ONLY (clarity-banner-options Req 4.5, 5):
             * the Clarity team publishes a platform's paid placements to its
             * owners. Same hidden-nav + route-guard contract as Monthly offers.
             */}
            {isAdmin && (
              <RailLink
                to="/banner-options"
                icon={<IconCampaigns className="nav-ico" />}
                label={t('nav.bannerOptions')}
              />
            )}
            {/*
             * Locations are ADMIN-ONLY (clarity-locations): the Clarity team
             * links a client's same-branch stores across platforms into one
             * place. Pure grouping metadata; same hidden-nav + route-guard
             * contract as the other admin tools.
             */}
            {isAdmin && (
              <RailLink
                to="/locations"
                icon={<IconConnections className="nav-ico" />}
                label={t('nav.locations')}
              />
            )}
            {/*
             * The campaigns OVERVIEW is ADMIN-ONLY (clarity-campaign-matching):
             * the admin's cross-client roll-up of which campaigns each client's
             * branches are enrolled in (inferred from orders). Same hidden-nav +
             * route-guard contract; the server enforces tenant/role independently.
             */}
            {isAdmin && (
              <RailLink
                to="/campaigns-overview"
                icon={<IconCampaigns className="nav-ico" />}
                label={t('nav.campaignsOverview')}
              />
            )}
            {/*
             * The payout-schedule surface is ADMIN-ONLY (clarity-payout-schedule
             * Req 5.1): the admin transcribes Talabat's monthly payout calendar,
             * saved shared across the platform's stores. Same hidden-nav +
             * route-guard contract; the server enforces admin-only independently.
             */}
            {isAdmin && (
              <RailLink
                to="/payout-schedule"
                icon={<IconPayouts className="nav-ico" />}
                label={t('nav.payoutSchedule')}
              />
            )}
            {/*
             * The platform-connections surface is ADMIN-ONLY
             * (clarity-platform-abstraction Req 5): the live per-platform
             * browser-session status + the shared re-auth flow. Same hidden-nav
             * + route-guard contract; the server enforces admin-only on
             * `GET /admin/connections` independently.
             */}
            {isAdmin && (
              <RailLink
                to="/connections"
                icon={<IconConnections className="nav-ico" />}
                label={t('nav.connections')}
              />
            )}
            {/*
             * INTERNAL dev-only backtest (clarity-payout-backtest): admin-gated
             * AND hidden behind a dev build — `import.meta.env.DEV` is false in a
             * production bundle, so the link never ships to prod.
             */}
            {isAdmin && import.meta.env.DEV && (
              <RailLink
                to="/payout-backtest"
                icon={<IconPayouts className="nav-ico" />}
                label={t('nav.payoutBacktest')}
              />
            )}
          </nav>
        )}

        {isAdmin && inClientContext && (
          <div className="rail__foot">
            <Link to="/clients" className="nav-item rail__back">
              <IconBack className="nav-ico ico-flip" />
              {t('rail.back')}
            </Link>
          </div>
        )}
      </aside>

      <header className="topbar">
        <div className="topbar__crumbs">
          {inClientContext ? (
            <>
              {isAdmin && <span>{t('nav.clients')}</span>}
              {isAdmin && <span className="topbar__crumb-sep">/</span>}
              <strong>{selectedClient?.name ?? session?.user.email}</strong>
            </>
          ) : (
            <strong>{t('rail.manage')}</strong>
          )}
        </div>
        <div className="topbar__right">
          {/*
           * The connection rollup NAMES every live platform with a status dot
           * (clarity-platform-abstraction Req 5.3), replacing the old single
           * Talabat badge. It reads the ADMIN `GET /admin/connections` endpoint
           * (a client-role user gets 403), and the Connections surface is an
           * admin concern (the mock is the admin topbar), so render it ONLY for
           * admins — mirroring how the admin "Manage" nav entries gate on
           * `isAdmin`. Not gated on client context: an admin sees platform health
           * at both the top level and inside a client. The server enforces
           * admin-only independently.
           */}
          {isAdmin && <ConnectionRollup />}
          <button type="button" className="ghost lang-toggle" onClick={toggleLocale} lang={localeTag}>
            {t('lang.toggle')}
          </button>
          <div className="topbar__who">
            <span className="email">{session?.user.email}</span>
            <span className="role">{role}</span>
          </div>
          <button type="button" className="ghost" onClick={signOut}>
            {t('topbar.signout')}
          </button>
        </div>
      </header>

      <main className="content">
        <Outlet />
      </main>

      {/*
       * The global Clarity Advisor chat (clarity-advisor-chat): a launcher +
       * slide-up panel floating over EVERY authenticated owner page. Owner-only
       * (it answers from one client's data); it renders null for an admin at the
       * top level. The same advisor answers on WhatsApp.
       */}
      <AdvisorChat />
    </div>
  );
}

/**
 * The client-workspace rail region (clarity-navigation-scope task 4.2): the scope
 * switcher (Req 2.1 placement — below "Viewing client", above the nav) followed
 * by the scope-driven surface nav. Both read the enclosing rail-side
 * `<ScopeProvider>` so the nav reflects exactly the scope the switcher shows.
 */
function WorkspaceRail() {
  const { t } = useI18n();
  const { scope, store, branches, isSingleBranch, isOnboardingLocked } = useScopeContext();
  const { activeClientId } = useClientContext();

  const navItems = buildNavItems({
    scope,
    clientId: activeClientId,
    isSingleBranch,
    isOnboardingLocked,
  });

  // New banner options count on the Campaigns item (clarity-banner-options Req
  // 8.1). Only when the scope resolves to exactly one store: a store scope, or a
  // one-branch all/brand scope. A locked branch's rail is onboarding-only, so
  // there is no Campaigns item to badge and no request is made. Shares the
  // Banners tab's query key, so the read is deduped with the Campaigns page.
  const singleStoreId =
    store?.id ?? (isSingleBranch && branches.length === 1 ? (branches[0]?.id ?? null) : null);
  const bannersEnabled = singleStoreId !== null && !isOnboardingLocked;
  const banners = useStoreBannerOptions(singleStoreId ?? '', bannersEnabled);
  const newBannerCount =
    bannersEnabled && banners.data?.status === 200 ? (banners.data.body.newCount ?? 0) : 0;

  return (
    <>
      {/* The switcher sits below "Viewing client" and above the nav (Req 2.1). */}
      <ScopeSwitcher />

      <nav className="rail__nav" aria-label={t('rail.workspace')}>
        <span className="rail__section-label">{t('rail.workspace')}</span>
        {navItems.map((item) => (
          <SurfaceLink
            key={item.id}
            item={item}
            hintLabel={t('nav.perStore')}
            hintAria={t('nav.perStore.aria')}
            count={item.id === 'campaigns' ? newBannerCount : 0}
          />
        ))}
        {/*
         * Workspace users (clarity-workspace-users): a CLIENT-LEVEL destination
         * like Brands, not a scope surface — so it is a hand-written link, not a
         * SURFACES entry. Visible to the workspace's own owner (self-serve) and
         * to an admin inside the client. Links to the non-scoped `/c/:id/users`.
         */}
        {activeClientId && (
          <RailLink
            to={`/c/${activeClientId}/users`}
            icon={<IconClients className="nav-ico" />}
            label={t('nav.workspaceUsers')}
          />
        )}
      </nav>
    </>
  );
}

/** A resolved rail surface item: where it links + whether it carries the hint. */
interface NavItem {
  id: SurfaceId;
  to: string;
  /** True for a branch-only surface shown at an all/brand scope (Req 4.3). */
  perStoreHint: boolean;
}

/**
 * Build the visible workspace nav from the SURFACES registry, driven by the
 * active scope + (for a single branch) its onboarding lifecycle. This is the
 * design §8 truth table in code:
 *
 * - LOCKED single branch (not-yet-onboarded, `isOnboardingLocked`) → ONLY the
 *   `onboarding` surface; the data + catalog surfaces are HIDDEN, not gated
 *   (Req 5.2). The client-level escape hatches (switcher, Brands, admin Back)
 *   live outside this list and stay available.
 * - SINGLE onboarded branch → all six surfaces, each linking straight to the
 *   store scope (Req 5.4 / 4.4).
 * - ALL / BRAND scope → the flexible surfaces normally; the branch-only surfaces
 *   shown WITH a "per store" hint (Req 4.3). They still LINK (to `<base>/catalog`
 *   etc.), where the BranchOnlyGuard narrows to one branch or prompts
 *   pick-a-branch — the hint just makes the constraint clear before clicking.
 */
export function buildNavItems({
  scope,
  clientId,
  isSingleBranch,
  isOnboardingLocked,
}: {
  scope: Scope;
  clientId: string | null;
  isSingleBranch: boolean;
  isOnboardingLocked: boolean;
}): NavItem[] {
  if (!clientId) return [];
  const base = scopeBase(clientId, scope);

  // A locked single branch offers only its onboarding surface (design §8).
  if (isOnboardingLocked) {
    return [{ id: 'onboarding', to: `${base}/onboarding`, perStoreHint: false }];
  }

  // A single onboarded branch (store scope, or a one-branch all/brand scope that
  // the switcher/guard resolves to a store) shows every surface directly.
  const singleBranch = scope.kind === 'store' || isSingleBranch;

  return SURFACES.map((surface) => {
    const branchOnly = SURFACE_CAPABILITY[surface.id] === 'branch-only';
    // Branch-only surfaces get the "per store" hint ONLY when the scope is NOT a
    // single branch (i.e. an all/brand aggregate). At a single branch they open
    // directly, so no hint.
    const perStoreHint = branchOnly && !singleBranch;
    return { id: surface.id, to: `${base}/${surface.id}`, perStoreHint };
  });
}

/** The scope's base path, matching the Req-1 URL shapes. */
function scopeBase(clientId: string, scope: Scope): string {
  switch (scope.kind) {
    case 'all':
      return `/c/${clientId}/all`;
    case 'brand':
      return `/c/${clientId}/brand/${scope.brandId}`;
    case 'store':
      return `/c/${clientId}/store/${scope.storeId}`;
  }
}

/**
 * A rail navigation link. `NavLink` resolves the active route and automatically
 * sets `aria-current="page"` on the active link — the existing
 * `.nav-item[aria-current='page']` CSS lights it teal, so the active state
 * follows the URL with no extra wiring.
 */
function RailLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink to={to} end className="nav-item">
      {icon}
      {label}
    </NavLink>
  );
}

/**
 * A workspace surface rail link built from the SURFACES registry. Carries the
 * surface icon + label, and — for a branch-only surface shown at an all/brand
 * scope — a "per store" hint chip whose TEXT names the constraint (Req 4.3,
 * WCAG AA: not colour alone). The link still navigates; the BranchOnlyGuard then
 * narrows or prompts.
 *
 * An optional `count` (> 0) renders a trailing count pill (the Campaigns item's
 * new banner options, clarity-banner-options Req 8.1). Like the hint, the
 * visible pill is aria-hidden and its meaning is folded into the link's
 * accessible name ("Campaigns — 2 new banner options").
 */
function SurfaceLink({
  item,
  hintLabel,
  hintAria,
  count = 0,
}: {
  item: NavItem;
  hintLabel: string;
  hintAria: string;
  count?: number;
}) {
  const { t } = useI18n();
  const surface = SURFACES.find((s) => s.id === item.id);
  const label = surface ? t(surface.labelKey as Parameters<typeof t>[0]) : item.id;
  const showCount = count > 0;
  const countAria = showCount
    ? t(count === 1 ? 'nav.campaigns.newCount.one' : 'nav.campaigns.newCount.many', { count })
    : null;
  const nameParts = [label];
  if (item.perStoreHint) nameParts.push(hintAria);
  if (countAria) nameParts.push(countAria);
  return (
    <NavLink
      to={item.to}
      end
      className={item.perStoreHint ? 'nav-item nav-item--per-store' : 'nav-item'}
      aria-label={nameParts.length > 1 ? nameParts.join(' — ') : undefined}
      data-surface-link={item.id}
      data-per-store={item.perStoreHint ? 'true' : undefined}
    >
      {surface?.icon}
      <span className="nav-item__label">{label}</span>
      {item.perStoreHint && (
        <span className="nav-item__hint" aria-hidden="true">
          {hintLabel}
        </span>
      )}
      {showCount && (
        <span className="nav-item__cnt" aria-hidden="true">
          {count}
        </span>
      )}
    </NavLink>
  );
}
